import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import useAdvanceReEligibility from 'features/advance/hooks/useAdvanceReEligibility';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import IconButton from 'shared/components/vxl/icon/IconButton/IconButton';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import { NavigationBar } from 'shared/components/vxl/navigation/NavigationBar/NavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import HyperlinkBaseText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkBaseText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import EmptyStateScreen from 'shared/patterns/vxl/EmptyStateScreen/EmptyStateScreen';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import {
  showZendeskArticle,
  zendeskArticleIds,
} from 'shared/utils/zendesk/zendeskUtils';

const AdvanceReEligibilityScreen = () => {
  const router = useRouter();
  const handleFAQClickedAnalytics = usePressableAnalytics({
    analyticsId: 'faqsClicked',
  });
  const dismissToTab = useDismissToRootTab();

  const goHome = () => {
    dismissToTab(Tabs.HOME);
  };

  const { id } = useLocalSearchParams<{ id?: string }>();

  const { advanceTitle, advanceDescription, loading, error } =
    useAdvanceReEligibility(id);

  const handleEligibilityRequirementClick = () => {
    handleFAQClickedAnalytics();
    try {
      showZendeskArticle(zendeskArticleIds.ADVANCE_ELIGIBILITY_REQUIREMENTS);
    } catch (error) {
      throw new Error(`Error showing Zendesk article: ${error}`);
    }
  };

  const { t: translatedText } = useTranslation('advance', {
    keyPrefix: 'whatIsVaStrings',
  });
  const hasError = error || !id;

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: translatedText('title'),
          header: () => (
            <NavigationBar
              title={translatedText('title')}
              primaryIcon={
                <Icon
                  iconKey="BACK"
                  iconSize="MD"
                  iconColor={theme.color.text.primary}
                  iconAccessibilityLabel="back-icon"
                />
              }
              primaryOnPress={() => router.replace('/')}
              trailingAccessories={
                !hasError ? (
                  <IconButton
                    iconKey="SETTINGS"
                    iconSize="MD"
                    iconButtonAccessibilityLabel="SETTINGS"
                    iconOnPress={() => {
                      router.push({
                        pathname: '/accounts/advance-details',
                        params: {
                          id,
                        },
                      });
                    }}
                  />
                ) : null
              }
            />
          ),
        }}
      />

      <ScreenTransitionOverlay
        isLoading={loading}
        LoadingComponent={<FullScreenLoading />}
        ErrorComponent={
          hasError ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={translatedText('errorTitle')}
              messageBody={translatedText('errorMessage')}
              primaryButton={{
                title: translatedText('doneButton'),
                onPress: goHome,
              }}
            />
          ) : null
        }
      />

      <View style={styles.emptyStateScreenView}>
        <EmptyStateScreen
          headlineText={advanceTitle || ''}
          content={
            <BodyBaseText numberOfLines={0} style={styles.bodyTextContainer}>
              {advanceDescription}{' '}
              <HyperlinkBaseText
                numberOfLines={0}
                onPress={handleEligibilityRequirementClick}
              >
                {translatedText('eligibilityRequirementsLabel')}
              </HyperlinkBaseText>
            </BodyBaseText>
          }
          illustrationKey="HOURGLASS_A"
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  bodyTextContainer: {
    marginTop: theme.spacing.s,
    textAlign: 'center',
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flexGrow: 1,
  },
  emptyStateScreenView: {
    flexGrow: 1,
    paddingHorizontal: theme.spacing.m,
  },
});

export default AdvanceReEligibilityScreen;
